import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { GetLocationInfo, SaveLocationInfo } from "../../services/locations";
import { TextFormField } from "../../components/textform/TextFormField.tsx";
import { Formik, Field, Form } from "formik";
import * as yup from "yup";
import CircularProgress from "@mui/material/CircularProgress";
import { MapPin, Video, Edit2, Eye, MinusSquare, Trash2 } from "react-feather";
import PopSnackbar from "../../components/snackbar/index.js";
import { Tooltip, Backdrop } from "@mui/material";
import { DownloadVideo, UploadVideo } from "../../services/video.js";
import { VideoPreview } from "../../components/modal/videopreview.js";
import AddVideoDialog from "../../components/modal/videodialog.js";
import MultiSelectFormField from "../../components/multiselectform/MultiSelectFormField.tsx";
import { GetAllDevices } from "../../services/devices.js";
import { GetTags } from "../../services/tag.js";
import { IconButton } from "@mui/material";
import AlertModal from "../../components/modal/index.js";

const styles = {
  select: {
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#f9f9f9",
    },
    "& .MuiInputLabel-root": { color: "#555" },
    "&.Mui-disabled .MuiOutlinedInput-root": { backgroundColor: "#f0f0f0" },
  },
};

export const validation = yup.object().shape({
  name: yup.string().required("Location name is required"),
});

export default function EditLocation() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [preview, setPreview] = useState(false);
  const [toastStatus, setToastStatus] = useState(false);
  const [toastDescription, setToastDescription] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deviceData, setDeviceData] = useState([]);
  const [tagData, setTagData] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [warningText, setWarningText] = useState("");

  const { id } = useParams();
  const { state } = useLocation();

  const handleStatus = (description, setStatus, setDescription) => {
    setStatus(true);
    setDescription(description);
    setTimeout(() => {
      setStatus(false);
      setDescription("");
    }, 5000);
  };

  async function GetDataInfo(idParam) {
    try {
      setLoading(true);
      const response = await GetLocationInfo(idParam);
      if (response.status !== "OK") {
        throw new Error(
          response.error || "Something went wrong, please try again later."
        );
      }
      setData(response.data);

      const responseDevices = await GetAllDevices();
      if (responseDevices.status !== "OK") {
        throw new Error(
          responseDevices.error ||
            "Something went wrong, please try again later."
        );
      }

      setDeviceData(responseDevices.data);

      const responseTags = await GetTags();
      if (responseTags.status !== "OK") {
        throw new Error(
          responseTags.error || "Something went wrong, please try again later."
        );
      }

      setTagData(responseTags.data);
    } catch (error) {
      handleStatus(error.message, setToastStatus, setToastDescription);
    } finally {
      setLoading(false);
    }
  }

  const handleViewVideo = async (videoID) => {
    try {
      // Fetch SAS URL directly
      const { url } = await DownloadVideo(videoID);

      // Use SAS URL directly for preview
      VideoPreview({
        url,
        onClose: () => {
          // No need to revoke Object URL as we're using the SAS URL directly
        },
      });
    } catch (error) {
      console.error("Error while previewing video:", error.message);
    }
  };

  const handleSubmitVideo = (videoData, values, setFieldValue) => {
    if (
      !videoData ||
      !videoData[0].file ||
      !videoData[0].title ||
      !videoData[0].duration
    ) {
      console.error("Incomplete video data provided.");
      return;
    }

    const updatedVideos = [...(values.videos || [])];

    const videoBlob = new Blob([videoData[0].file], {
      type: videoData[0].file.type,
    });
    // const videoUrl = URL.createObjectURL(videoBlob);

    const newVideoData = {
      ID: videoData[0].id || `temp-${new Date().getTime()}`, // Use `id` consistently
      title: videoData[0].title,
      file: videoBlob,
      duration: videoData[0].duration,
    };

    updatedVideos.push(newVideoData);

    setFieldValue("videos", updatedVideos);
  };

  const handleSelectExistingVideo = async (
    videoArray,
    values,
    setFieldValue
  ) => {
    const updatedVideos = [...(values.videos || [])];

    for (const video of videoArray) {
      if (!updatedVideos.some((v) => v.id === video.id)) {
        // Fetch SAS URL directly
        const { url } = await DownloadVideo(video.id);

        updatedVideos.push({
          ID: video.id,
          title: video.title,
          url, // Use SAS URL directly
          duration: video.duration,
        });
      }
    }

    // Update form field with updated video list
    setFieldValue("videos", updatedVideos);
  };

  const handleRemoveVideo = (videoId, values, setFieldValue) => {
    const updatedVideos = values.videos.filter((video) => video.ID !== videoId);
    setFieldValue("videos", updatedVideos);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const FormSection = ({ icon, title, children }) => (
    <div
      style={{
        marginBottom: "20px",
        padding: "20px",
        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
        borderRadius: "8px",
        backgroundColor: "white",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          columnGap: "10px",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>{icon}</div>
        <div
          style={{
            fontSize: "1.75rem",
            fontFamily: "SeagoeUIBold",
          }}
        >
          {title}
        </div>
      </div>
      <div
        className="form-section-content"
        style={{ display: "flex", flexDirection: "column" }}
      >
        {children}
      </div>
    </div>
  );

  async function SaveLocationData(values) {
    try {
      setLoading(true);

      // Prepare devices with associated tags
      const formattedDevices = values.devices.map((deviceId, index) => ({
        device_id: deviceId,
        tags: (values.tags[index] || []).map((tagId) => ({ id: tagId })),
      }));

      // Separate existing and new (temp) videos
      const existingVideos = values.videos.filter(
        (v) => v.ID && !String(v.ID).startsWith("temp-")
      );
      const newVideos = values.videos.filter(
        (v) => !v.ID || String(v.ID).startsWith("temp-")
      );

      // For the initial location update, only include existing videos
      const payload = {
        name: values.name,
        county: values.county,
        city: values.city,
        address: values.address || "",
        observations: values.observations || "",
        contact1: values.contact1 || "",
        contact2: values.contact2 || "",
        contact3: values.contact3 || "",
        contact4: values.contact4 || "",
        contact5: values.contact5 || "",
        devices: formattedDevices,
        videos: existingVideos.map((video) => ({ id: video.ID })),
      };

      const jsonPayload = JSON.stringify(payload);
      const response = await SaveLocationInfo(jsonPayload, id);

      if (response.status !== "OK") {
        throw new Error(
          response.error || "Something went wrong, please try again later."
        );
      }

      if (response.warning && response.warning.length > 0) {
        const warnings = Array.isArray(response.warning)
          ? response.warning.join("\n")
          : String(response.warning);
        setWarningText(warnings);
        setWarningModalOpen(true);
      }

      // Now that the location is updated with existing videos,
      // handle uploading the new (temp) videos.
      // This is similar to what you do in the add scenario.
      const updatedLocation = response.data; // Make sure the response includes the updated location data with ID

      for (const video of newVideos) {
        // This matches the logic from your add scenario
        const videoData = new FormData();
        videoData.append("file", video.file);
        videoData.append("title", video.title);
        videoData.append("duration", video.duration);
        // videoData.append("is_landscape", video.is_landscape);
        videoData.append("categories", JSON.stringify([2]));
        videoData.append("location_ids", JSON.stringify([updatedLocation.ID]));

        const uploadResponse = await UploadVideo(videoData);
        if (uploadResponse.status !== "OK") {
          throw new Error(
            uploadResponse.error || "Failed to upload video for location."
          );
        }
      }

      handleStatus(
        "Location and video updated successfully!",
        setToastStatus,
        setToastDescription
      );

      await GetDataInfo(id);
    } catch (error) {
      handleStatus(error.message, setToastStatus, setToastDescription);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    GetDataInfo(id);
  }, []);

  useEffect(() => {
    if (state && state.preview) {
      setPreview(state.preview); // Should log "Hello, World!"
    }
  }, [state]);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        padding: "20px",
        paddingBottom: "100px",
      }}
    >
      {toastStatus && <PopSnackbar message={toastDescription} />}
      <div
        className="form-container"
        style={{ width: "100%", opacity: loading ? 0.5 : 1 }}
      >
        {loading ? (
          <CircularProgress />
        ) : (
          <Formik
            validationSchema={validation}
            initialValues={{
              name: data?.name || "",
              county: data?.county || "",
              city: data?.city || "",
              address: data?.address || "",
              observations: data?.observations || "",
              contact1: data?.contact1 || "",
              contact2: data?.contact2 || "",
              contact3: data?.contact3 || "",
              contact4: data?.contact4 || "",
              contact5: data?.contact5 || "",
              videos: data?.videos || [],
              devices: data?.devices.map((device) => device.device_id) || [],
              tags:
                data?.devices.map((device) =>
                  device.tags.map((tag) => tag.ID)
                ) || [],
            }}
            onSubmit={(values) => SaveLocationData(values)}
          >
            {({ setFieldValue, setFieldTouched, values }) => (
              <Form
                id="location-form"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                    gap: "20px",
                    alignItems: "flex-start",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <FormSection
                      title="Detalii generale"
                      icon={<MapPin size={"1.5rem"} />}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "10px",
                        }}
                      >
                        <Field
                          name="name"
                          label="Nume locatie"
                          disabled={preview}
                          component={TextFormField}
                          style={{ width: "100%" }}
                          sx={styles.select}
                          placeholder="e.g., Regina Maria"
                        />
                        <Field
                          name="county"
                          label="Judet"
                          component={TextFormField}
                          sx={styles.select}
                          style={{ width: "100%" }}
                          placeholder="e.g., Bucuresti"
                        />
                        <Field
                          name="city"
                          label="Oras"
                          component={TextFormField}
                          sx={styles.select}
                          style={{ width: "100%" }}
                          placeholder="e.g., Sector 1"
                        />
                        <Field
                          name="address"
                          label="Adresa"
                          disabled={preview}
                          component={TextFormField}
                          sx={styles.select}
                          style={{ width: "100%" }}
                          placeholder="Introdu adresa locatiei"
                        />
                      </div>

                      {/* Sublocations Section */}
                    </FormSection>
                    <FormSection
                      title="Aloca playere"
                      icon={<MapPin size={"1.5rem"} />}
                    >
                      {/* Select All button */}
                      <div style={{ marginBottom: "10px", textAlign: "right" }}>
                        <button
                          type="button"
                          style={{
                            background: "none",
                            border: "1px solid #ccc",
                            padding: "5px 10px",
                            borderRadius: "4px",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            const filteredDeviceIds = deviceData
                              .filter(
                                (device) =>
                                  (device.location_id === null ||
                                    device.location_id === Number(id)) &&
                                  device.name !== null
                              )
                              .map((device) => device.device_id);

                            const areAllSelected = filteredDeviceIds.every(
                              (deviceId) => values.devices.includes(deviceId)
                            );

                            if (areAllSelected) {
                              setFieldValue(
                                "devices",
                                values.devices.filter(
                                  (deviceId) =>
                                    !filteredDeviceIds.includes(deviceId)
                                )
                              ); // deselect only the filtered devices
                            } else {
                              setFieldValue(
                                "devices",
                                Array.from(
                                  new Set([
                                    ...values.devices,
                                    ...filteredDeviceIds,
                                  ])
                                )
                              ); // add missing devices (merge unique)
                            }
                          }}
                        >
                          {deviceData
                            .filter(
                              (device) =>
                                (device.location_id === null ||
                                  device.location_id === Number(id)) &&
                                device.name !== null
                            )
                            .every((device) =>
                              values.devices.includes(device.device_id)
                            )
                            ? "Deselecteaza toate"
                            : "Selecteaza toate"}
                        </button>
                      </div>

                      {/* MultiSelect Field */}
                      <Field
                        name="devices"
                        label="Selecteaza playere"
                        component={MultiSelectFormField}
                        options={deviceData
                          .filter(
                            (device) =>
                              (device.location_id === null ||
                                device.location_id === Number(id)) &&
                              device.name !== null
                          )
                          .map((device) => ({
                            value: device.device_id,
                            label: device?.name,
                          }))}
                        style={{ width: "100%" }}
                        sx={styles.select}
                      />

                      {/* Device Tags */}
                      {values.devices?.map((deviceId, index) => {
                        const device = deviceData.find(
                          (d) => d.device_id === deviceId
                        );
                        return (
                          <div
                            key={index}
                            style={{
                              marginTop: "20px",
                              padding: "10px",
                              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                              borderRadius: "8px",
                              backgroundColor: "white",
                              border: "1px solid #ddd",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "1.25rem",
                                  marginBottom: "10px",
                                }}
                              >
                                {device?.name}
                              </div>
                              <div>
                                <IconButton
                                  onClick={() => {
                                    const updatedDevices =
                                      values.devices.filter(
                                        (device) => device !== deviceId
                                      );
                                    setFieldValue("devices", updatedDevices);
                                  }}
                                  style={{
                                    margin: 0,
                                    padding: 0,
                                  }}
                                >
                                  <Trash2
                                    size={20}
                                    color="black"
                                    style={{ cursor: "pointer" }}
                                  />
                                </IconButton>
                              </div>
                            </div>
                            <Field
                              disabled={preview}
                              name={`tags[${index}]`}
                              label="Selecteaza taguri pentru player"
                              component={MultiSelectFormField}
                              options={tagData?.map((tag) => ({
                                value: tag.ID,
                                label: tag.name,
                              }))}
                              style={{ width: "100%" }}
                              sx={styles.select}
                            />
                          </div>
                        );
                      })}
                    </FormSection>

                    <FormSection
                      title="Spoturi de locatie"
                      icon={<Video size={"1.5rem"} />}
                    >
                      {(values.videos || []).length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "10px",
                            padding: "10px",
                            backgroundColor: "#f9f9f9",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                          }}
                        >
                          {values.videos?.map((video, index) => (
                            <div
                              key={video.id || `temp-${index}`} // Use `id` consistently
                              style={{
                                flex: "1 1 45%",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                padding: "10px",
                                backgroundColor: "white",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                              }}
                            >
                              <div
                                style={{
                                  flex: "1 1 auto",
                                  display: "flex",
                                  columnGap: "10px",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <span
                                  style={{
                                    flex: "1 1 auto",
                                    margin: "0 0 5px 0",
                                    fontSize: "1em",
                                    fontFamily: "SeagoeUI",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {video.title}.mp4
                                </span>
                                {!preview && (
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "10px",
                                      flexShrink: 0, // prevent icons from shrinking
                                    }}
                                  >
                                    {!String(video.ID).startsWith("temp-") && (
                                      <>
                                        <Tooltip title={"Editeaza video"}>
                                          <Edit2
                                            size={20}
                                            style={{
                                              color: "gray",
                                              cursor: "pointer",
                                            }}
                                            onClick={() =>
                                              navigate(`/videos/${video.ID}`)
                                            }
                                          />
                                        </Tooltip>
                                        <Tooltip
                                          title={"Previzualizare rapida"}
                                        >
                                          <Eye
                                            size={20}
                                            style={{
                                              color: "gray",
                                              cursor: "pointer",
                                            }}
                                            onClick={() =>
                                              handleViewVideo(video.ID)
                                            }
                                          />
                                        </Tooltip>
                                      </>
                                    )}
                                    <Tooltip title={"Sterge video"}>
                                      <MinusSquare
                                        size={20}
                                        style={{
                                          color: "red",
                                          cursor: "pointer",
                                        }}
                                        onClick={() =>
                                          handleRemoveVideo(
                                            video.ID,
                                            values,
                                            setFieldValue
                                          )
                                        }
                                      />
                                    </Tooltip>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Button for adding videos */}
                      <button
                        type="button"
                        className="add-video-button"
                        style={{
                          opacity: preview ? 0.5 : 1,
                        }}
                        disabled={loading || preview}
                        onClick={() => setIsModalOpen(true)}
                      >
                        Adauga spoturi
                      </button>
                      <AddVideoDialog
                        open={isModalOpen}
                        onClose={handleModalClose}
                        onSubmit={(videoData) =>
                          handleSubmitVideo(videoData, values, setFieldValue)
                        }
                        onSelect={(videoArray) =>
                          handleSelectExistingVideo(
                            videoArray,
                            values,
                            setFieldValue
                          )
                        }
                        instantUpload={false}
                        canAddVideo={false}
                      />
                    </FormSection>
                  </div>

                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <FormSection
                      title="Detalii contact"
                      icon={<MapPin size={"1.5rem"} />}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "10px",
                        }}
                      >
                        <Field
                          name="observations"
                          label="Observatii"
                          disabled={preview}
                          component={TextFormField}
                          sx={styles.select}
                          style={{ width: "100%" }}
                          placeholder="Observatii despre locatie"
                          multiline
                          rows={3}
                        />
                        <Field
                          name="contact1"
                          label="Contact 1"
                          disabled={preview}
                          component={TextFormField}
                          sx={styles.select}
                          style={{ width: "100%" }}
                          placeholder="Contact 1"
                        />
                        <Field
                          name="contact2"
                          label="Contact 2"
                          disabled={preview}
                          component={TextFormField}
                          sx={styles.select}
                          style={{ width: "100%" }}
                          placeholder="Contact 2"
                        />
                        <Field
                          name="contact3"
                          label="Contact 3"
                          disabled={preview}
                          component={TextFormField}
                          sx={styles.select}
                          style={{ width: "100%" }}
                          placeholder="Contact 3"
                        />
                        <Field
                          name="contact4"
                          label="Contact 4"
                          disabled={preview}
                          component={TextFormField}
                          sx={styles.select}
                          style={{ width: "100%" }}
                          placeholder="Contact 4"
                        />
                        <Field
                          name="contact5"
                          label="Contact 5"
                          disabled={preview}
                          component={TextFormField}
                          sx={styles.select}
                          style={{ width: "100%" }}
                          placeholder="Contact 5"
                        />
                      </div>
                    </FormSection>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>

      <AlertModal
        text={warningText}
        open={warningModalOpen}
        handleClose={() => setWarningModalOpen(false)}
        showYesButton={false}
        showNoButton={false}
        showOkButton={true}
      />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loadingPreview}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          padding: "30px",
          borderTop: "1px solid rgba(0, 0, 0, 0.06)",
          zIndex: 1000,
          display: "flex",
          justifyContent: "center",
          gap: "16px",
        }}
      >
        {preview ? (
          <button
            onClick={() => navigate(-1)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#000",
              color: "white",
              padding: "14px 32px",
              border: "none",
              borderRadius: "8px",
              fontFamily: "SeagoeUIBold",
              cursor: "pointer",
              fontSize: "1em",
              minWidth: "200px",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow: "0 4px 14px rgba(0, 0, 0, 0.15)",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#1e3a8a";
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 8px 25px rgba(30, 58, 138, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#000";
              e.target.style.transform = "translateY(0px)";
              e.target.style.boxShadow = "0 4px 14px rgba(0, 0, 0, 0.15)";
            }}
          >
            ← Inapoi
          </button>
        ) : (
          <>
            <button
              onClick={() => navigate(-1)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f8f9fa",
                color: "#495057",
                padding: "14px 32px",
                border: "2px solid #e9ecef",
                borderRadius: "8px",
                fontFamily: "SeagoeUIBold",
                cursor: "pointer",
                fontSize: "1em",
                minWidth: "180px",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#e9ecef";
                e.target.style.borderColor = "#dee2e6";
                e.target.style.transform = "translateY(-1px)";
                e.target.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#f8f9fa";
                e.target.style.borderColor = "#e9ecef";
                e.target.style.transform = "translateY(0px)";
                e.target.style.boxShadow = "none";
              }}
            >
              Anuleaza
            </button>
            <button
              disabled={loading}
              form="location-form"
              type="submit"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: loading ? "#ccc" : "#000",
                color: "white",
                fontFamily: "SeagoeUIBold",
                padding: "14px 32px",
                border: "none",
                borderRadius: "8px",
                cursor: loading ? "not-allowed" : "pointer",
                fontSize: "1em",
                minWidth: "220px",
                opacity: loading ? 0.7 : 1,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: loading ? "none" : "0 4px 14px rgba(0, 0, 0, 0.15)",
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = "#1e3a8a";
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow =
                    "0 8px 25px rgba(30, 58, 138, 0.3)";
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = "#000";
                  e.target.style.transform = "translateY(0px)";
                  e.target.style.boxShadow = "0 4px 14px rgba(0, 0, 0, 0.15)";
                }
              }}
            >
              {loading ? "Se salveaza..." : "Salveaza modificari"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
